<script>
import request from '@/utils/request.js'

export default {
  data() {
    return {
      currentTheme: 'dark'
    }
  },
  onShow() {
    // 应用回到前台时，刷新用户头像/昵称，确保 H5/小程序以后端为准保持一致
    this.refreshUserInfo();
  },
  onLaunch: function() {
    console.log('🚀 App Launch - Env:', process.env.NODE_ENV)
    
    // #ifdef H5
    try {
      const hideTabBarResult = uni.hideTabBar && uni.hideTabBar();
      if (hideTabBarResult && typeof hideTabBarResult.catch === 'function') {
        hideTabBarResult.catch(() => {});
      }
    } catch (e) {}
    // #endif

    // #ifdef MP-WEIXIN
    // 小程序版本更新检测
    this.checkAppUpdate();
    // #endif
    
    // #ifdef H5
    // H5 视口高度适配（解决 Safari/微信浏览器底部栏问题）
    this.setupViewportHeight();
    // H5 全局缩放：保持 100%
    try {
      document.documentElement.style.zoom = '1';
    } catch (e) {}
    // #endif
    
    // 加载保存的主题 (默认改为 dark)
    const savedTheme = uni.getStorageSync('theme') || 'dark';
    this.currentTheme = savedTheme;
    this.applyTheme(savedTheme);
    
    // 监听主题变更事件
    uni.$on('themeChange', (data) => {
      this.currentTheme = data.theme;
      this.applyTheme(data.theme);
    });
    
    // 静默登录检查完成后再加载全局配置，确保 model_config 按当前用户权限返回
    this.checkAutoLogin().finally(() => {
      this.loadGlobalConfig();
    });
  },
  methods: {
    async refreshUserInfo() {
      if (!request.isLoggedIn()) return;
      try {
        const me = await request.get('/api/v1/user/me', {}, { showLoading: false, showError: false, timeout: 8000 });
        if (me) {
          uni.setStorageSync('userInfo', me);
          uni.$emit('userInfoUpdated', me);
        }
      } catch (e) {
        // 静默失败即可：网络/后端异常不影响主流程
      }
    },
    // #ifdef MP-WEIXIN
    // 小程序版本更新检测
    checkAppUpdate() {
      if (typeof wx !== 'undefined' && wx.getUpdateManager) {
        const updateManager = wx.getUpdateManager();
        
        updateManager.onCheckForUpdate((res) => {
          console.log('📦 检查更新结果:', res.hasUpdate ? '有新版本' : '已是最新');
        });
        
        updateManager.onUpdateReady(() => {
          wx.showModal({
            title: '更新提示',
            content: '新版本已就绪，是否重启应用？',
            success: (res) => {
              if (res.confirm) {
                updateManager.applyUpdate();
              }
            }
          });
        });
        
        updateManager.onUpdateFailed(() => {
          console.warn('❌ 新版本下载失败');
        });
      }
    },
    // #endif
    
    // #ifdef H5
    // H5 视口高度适配
    setupViewportHeight() {
      const setVH = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
      setVH();
      window.addEventListener('resize', setVH);
      window.addEventListener('orientationchange', () => {
        setTimeout(setVH, 100);
      });
    },
    // #endif
    
    // 静默登录检查
    async checkAutoLogin() {
      // 检查本地是否有有效 Token
      if (request.isLoggedIn()) {
        console.log('🔍 检测到本地 Token，验证有效性...');
        
        // 调用后端验证 Token 是否有效
        const isValid = await request.validateToken();
        
        if (isValid) {
          console.log('✅ Token 有效，静默登录成功');

          // 拉取最新用户信息（头像/昵称等），避免各端缓存不一致
          this.refreshUserInfo();
          
          // #ifdef H5
          // H5 模式下，使用 window.location 获取当前路径
          // 支持 hash 模式 (/#/pages/xxx) 和 history 模式 (/pages/xxx)
          let currentPath = window.location.hash || window.location.pathname || '';
          // 处理 hash 模式：去掉开头的 #
          if (currentPath.startsWith('#')) {
            currentPath = currentPath.substring(1);
          }
          // 去掉开头的 /
          if (currentPath.startsWith('/')) {
            currentPath = currentPath.substring(1);
          }
          console.log('📍 当前 H5 路径:', currentPath, '完整 URL:', window.location.href);
          
          // 已登录用户不应停留在登录页或封面首页，统一进入创作页
          const isLoginPage = currentPath.includes('login');
          const isHomePage = currentPath.includes('home') || currentPath === '' || currentPath === '/';

          if (isLoginPage || isHomePage) {
            console.log('🔄 已登录，进入创作页');
            setTimeout(() => {
              uni.reLaunch({
                url: '/pages/create/create',
                fail: () => {
                  uni.switchTab({ url: '/pages/create/create' });
                }
              });
            }, 100);
          } else {
            console.log('📌 刷新时保持当前页面:', currentPath);
          }
          // #endif
          
          // #ifndef H5
          // 小程序模式下使用 getCurrentPages
          const pages = getCurrentPages();
          const currentRoute = pages.length > 0 ? pages[0].route : '';
          
          if (currentRoute === 'pages/login/index' || currentRoute === 'pages/home/home' || currentRoute === '') {
            setTimeout(() => {
              uni.reLaunch({
                url: '/pages/create/create',
                fail: () => {
                  uni.switchTab({ url: '/pages/create/create' });
                }
              });
            }, 100);
          }
          // #endif
        } else {
          console.log('❌ Token 无效或已过期，清除登录状态');
          request.removeToken();
        }
      } else {
        console.log('📝 未检测到有效 Token，需要登录');
      }
    },
    
    async loadGlobalConfig() {
      try {
        const isLoggedIn = request.isLoggedIn();
        const config = await request.get('/api/v1/config/global', {}, {
          timeout: 20000,
          showError: false,
          needAuth: isLoggedIn,
        });
        if (config) {
          uni.setStorageSync('sys_config', config);
          uni.setStorageSync('sys_config_timestamp', Date.now());
          console.log('全局配置加载成功', config);
        }
      } catch (e) {
        console.error('全局配置加载失败（不影响使用）', e);
      }
    },
    
    // 应用主题到全局CSS变量
    applyTheme(theme) {
      console.log('切换主题:', theme);
      const isDark = theme === 'dark';
      
      // 1. H5 专属 DOM 状态更新
      // #ifdef H5
      if (typeof document !== 'undefined') {
        const root = document.documentElement;
        // 同时在 html 和 body 上设置类名，确保覆盖全面
        // 注意：项目中存在两套类名约定，需要同时添加以确保所有组件都能响应
        root.classList.remove('light-mode', 'dark-mode', 'theme-light', 'theme-dark');
        root.classList.add(isDark ? 'dark-mode' : 'light-mode');
        root.classList.add(isDark ? 'theme-dark' : 'theme-light');
        document.body.classList.remove('light-mode', 'dark-mode', 'theme-light', 'theme-dark');
        document.body.classList.add(isDark ? 'dark-mode' : 'light-mode');
        document.body.classList.add(isDark ? 'theme-dark' : 'theme-light');
        
        if (!isDark) {
          // 浅色模式 - 极致纯净白天模式 (Hex: #FFFFFF)
          root.style.setProperty('--bg-primary', '#ffffff');
          root.style.setProperty('--bg-secondary', '#f8fafc');
          root.style.setProperty('--bg-tertiary', '#f1f5f9');
          root.style.setProperty('--bg-base', '#ffffff');
          root.style.setProperty('--bg-surface', '#ffffff');
          root.style.setProperty('--bg-elevated', '#ffffff');
          root.style.setProperty('--bg-hover', '#f3f4f6');
          root.style.setProperty('--text-primary', '#000000');   // 纯黑色
          root.style.setProperty('--text-secondary', '#374151'); // 稍深的中灰色
          root.style.setProperty('--text-muted', '#6B7280');    // 辅助深灰
          root.style.setProperty('--icon-color', '#374151');
          root.style.setProperty('--input-bg', '#ffffff');
          root.style.setProperty('--border-color', '#E5E7EB');
          root.style.setProperty('--border-color-light', '#F3F4F6');
          root.style.setProperty('--divider-color', '#F3F4F6');
          root.style.setProperty('--color-primary', '#6366f1');
          root.style.setProperty('--gradient-primary', 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)');
          root.style.setProperty('--shadow-sm', '0 1px 2px rgba(0, 0, 0, 0.05)');
          root.style.setProperty('--shadow-md', '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)');
          root.style.setProperty('--shadow-lg', '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)');
          
          // Chat Bubbles - Light Mode
          root.style.setProperty('--chat-ai-bg', 'transparent');
          root.style.setProperty('--chat-ai-text', '#000000');
          root.style.setProperty('--chat-ai-border', 'transparent');
          root.style.setProperty('--chat-user-bg', '#f3f4f6');
          root.style.setProperty('--chat-user-text', '#000000');
          
          // Sidebar - Light Mode
          root.style.setProperty('--sidebar-bg', '#ffffff');
          root.style.setProperty('--session-active-bg', '#f3f4f6');
          root.style.setProperty('--session-active-text', '#6366f1');

          // Cognitive Extraction - Light Mode
          root.style.setProperty('--cognitive-input-bg', '#ffffff');
          root.style.setProperty('--cognitive-input-text', '#000000');
          root.style.setProperty('--cognitive-input-border', '#E5E7EB');

          // Capsule Text Color - Light Mode
          root.style.setProperty('--chat-op-text', '#4B5563'); // 中灰色
        } else {
          // 深色模式 (恢复默认值)
          root.style.setProperty('--bg-primary', '#0a0a0f');
          root.style.setProperty('--bg-secondary', '#0d0d12');
          root.style.setProperty('--bg-tertiary', '#161620');
          root.style.setProperty('--bg-base', '#000000');
          root.style.setProperty('--bg-surface', '#0e0e11');
          root.style.setProperty('--bg-elevated', '#0e0e11');
          root.style.setProperty('--bg-hover', '#2d2d4a');
          root.style.setProperty('--text-primary', '#f8fafc');
          root.style.setProperty('--text-secondary', '#94a3b8');
          root.style.setProperty('--text-muted', '#64748b');
          root.style.setProperty('--icon-color', '#94a3b8');
          root.style.setProperty('--input-bg', '#0e0e11');
          root.style.setProperty('--border-color', 'rgba(255, 255, 255, 0.05)');
          root.style.setProperty('--border-color-light', 'rgba(148, 163, 184, 0.08)');
          root.style.setProperty('--divider-color', 'rgba(255, 255, 255, 0.1)');
          root.style.setProperty('--color-primary', '#6366f1');
          root.style.setProperty('--gradient-primary', 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)');
          root.style.setProperty('--shadow-sm', '0 2rpx 8rpx rgba(0, 0, 0, 0.3)');
          root.style.setProperty('--shadow-md', '0 8rpx 24rpx rgba(0, 0, 0, 0.4)');
          root.style.setProperty('--shadow-lg', '0 16rpx 48rpx rgba(0, 0, 0, 0.5)');
          
          // Chat Bubbles - Dark Mode (Preserve existing)
          root.style.setProperty('--chat-ai-bg', '#18181b');
          root.style.setProperty('--chat-ai-text', '#e2e8f0');
          root.style.setProperty('--chat-ai-border', '#27272a');
          root.style.setProperty('--chat-user-bg', '#6366f1'); /* var(--color-primary) */
          root.style.setProperty('--chat-user-text', '#ffffff');
          
          // Sidebar - Dark Mode
          root.style.setProperty('--sidebar-bg', '#000000');
          root.style.setProperty('--session-active-bg', '#2d2d4a');
          root.style.setProperty('--session-active-text', '#e0e7ff');

          // Cognitive Extraction - Dark Mode
          root.style.setProperty('--cognitive-input-bg', 'rgba(0, 0, 0, 0.3)');
          root.style.setProperty('--cognitive-input-text', '#e2e8f0');
          root.style.setProperty('--cognitive-input-border', 'rgba(255, 255, 255, 0.1)');

          // Capsule Text Color - Dark Mode
          root.style.setProperty('--chat-op-text', '#818cf8'); // 紫色 / 靛蓝色
        }
      }
      // #endif
      
      // 2. 原生 UI 组件同步 (小程序/App)
      // 强制 Light Mode 顶部栏也为黑色背景 + 白色文字 (响应用户需求)
      const navBgColor = '#000000'; 
      const navFrontColor = '#ffffff';
      
      const tabColor = isDark ? '#94a3b8' : '#64748B';
      const tabSelectedColor = isDark ? '#ffffff' : '#6366f1';
      
      try {
        const setNavigationBarColorResult = uni.setNavigationBarColor({
          frontColor: navFrontColor,
          backgroundColor: navBgColor,
          animation: { duration: 300, timingFunc: 'easeIn' }
        });
        if (setNavigationBarColorResult && typeof setNavigationBarColorResult.catch === 'function') {
          setNavigationBarColorResult.catch(() => {});
        }
      } catch (e) {}
      
      try {
        const setTabBarStyleResult = uni.setTabBarStyle({
          color: tabColor,
          selectedColor: tabSelectedColor,
          backgroundColor: navBgColor,
          borderStyle: isDark ? 'white' : 'black'
        });
        if (setTabBarStyleResult && typeof setTabBarStyleResult.catch === 'function') {
          setTabBarStyleResult.catch(() => {});
        }
      } catch (e) {}
    }
  },
  onShow: function() {
    console.log('App Show')
  },
  onHide: function() {
    console.log('App Hide')
  }
}
</script>

<style>
/* 彻底隐藏全局滚动条 */
::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

/* Firefox 隐藏滚动条 */
* {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

/* 创作页对话区：完全隐藏原生滚动条，使用自定义滑块 */
.message-scroll-native {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

.message-scroll-native::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
  background: transparent !important;
}

.message-scroll-native::-webkit-scrollbar-thumb,
.message-scroll-native::-webkit-scrollbar-track,
.message-scroll-native::-webkit-scrollbar-button,
.message-scroll-native::-webkit-scrollbar-corner {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

/* 消除 uni-app H5 端的默认占位和白闪 */
uni-tabbar,
.uni-tabbar {
  display: none !important;
  height: 0 !important;
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;
}

.uni-app--show-tabbar uni-page-wrapper,
.uni-app--showtabbar uni-page-wrapper,
.uni-app--show-tabbar uni-page,
.uni-app--showtabbar uni-page,
uni-page-wrapper,
uni-page-body,
uni-page {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  background-color: transparent !important; /* 让背景透出到 html/body */
}

body {
  padding-bottom: 0 !important;
  /* 使用变量确保背景跟随主题，防止加载瞬间的白底 */
  background-color: var(--bg-base, #0d0d12) !important;
}

/* 针对 iOS 安全区域的白条修正 */
.uni-page-wrapper::after,
uni-page-wrapper::after {
  display: none !important;
  content: none !important;
  height: 0 !important;
  background: transparent !important;
  border: 0 !important;
}

uni-page::after,
uni-page-body::after {
  display: none !important;
  content: none !important;
  height: 0 !important;
}

/* 兜底：清除 tabbar 页面底部保留高度 */
.uni-app--show-tabbar,
.uni-app--showtabbar,
.uni-page,
uni-page,
.uni-page-wrapper,
uni-page-wrapper {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

/*
 * AI 文案智能体 - 全局设计系统
 * 深色高级感主题，适配 H5 和微信小程序
 */

/* 全局重置与视口锁定 */
html, body, page {
  margin: 0;
  padding: 0;
  /* 强制默认深色背景，解决底部白条和加载闪烁 */
  background-color: #0d0d12 !important;
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  overflow-x: hidden;
  -webkit-text-size-adjust: 100%;
}

/* 强制覆盖 uni-app 根节点链路背景，消除底部残留白条 */
#app,
#app > .uni-app,
.uni-app,
.uni-app--showtabbar,
uni-app,
uni-page,
uni-page-body,
uni-page-wrapper {
  background-color: var(--bg-base, #0d0d12) !important;
}

/* 根容器高度铺满视口，避免底部出现额外空白层 */
html,
body,
#app,
#app > .uni-app,
.uni-app {
  height: 100%;
  min-height: 100vh;
}

/* 仅在明确开启浅色模式时切换背景 */
html.light-mode, 
body.light-mode, 
page.light-mode,
.light-mode {
  background-color: #ffffff !important;
}

/* 浅色模式下同样覆盖 uni-app 根链路 */
html.light-mode #app,
html.light-mode #app > .uni-app,
html.light-mode .uni-app,
html.light-mode .uni-app--showtabbar,
html.light-mode uni-app,
html.light-mode uni-page,
html.light-mode uni-page-body,
html.light-mode uni-page-wrapper,
body.light-mode #app,
body.light-mode #app > .uni-app,
body.light-mode .uni-app,
body.light-mode .uni-app--showtabbar,
body.light-mode uni-app,
body.light-mode uni-page,
body.light-mode uni-page-body,
body.light-mode uni-page-wrapper {
  background-color: #ffffff !important;
}

/* uni-app 页面容器默认深色背景 */
uni-page-body,
.uni-page-body,
uni-page-wrapper,
.uni-page-wrapper {
  background-color: #0d0d12 !important;
  min-height: 100vh;
}

/* 浅色模式下页面容器背景覆盖 */
.light-mode uni-page-body,
.light-mode .uni-page-body,
.light-mode uni-page-wrapper,
.light-mode .uni-page-wrapper,
html.light-mode uni-page-body,
html.light-mode uni-page-wrapper {
  background-color: #ffffff !important;
}

/* ============================================
   H5 端滚动条隐藏与触感优化
   ============================================ */

/* H5 隐藏滚动条 - 与小程序体验对齐 */
/* H5 隐藏滚动条 - 与小程序体验对齐 */
/* 注释掉强制隐藏滚动条，以便桌面端用户可以看到滚动进度 */
/*
::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

page, view, scroll-view, textarea {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
*/

/* iOS Safari 原生触感滚动 */
.scroll-container,
.list-scroll,
.message-scroll,
scroll-view {
  -webkit-overflow-scrolling: touch;
}

/* 全局盒模型 */
/* * 不支持通配符，需手动指定元素 */
page, view, scroll-view, swiper, swiper-item, cover-view, cover-image, icon, text, rich-text, progress, button, checkbox, form, input, label, picker, picker-view, radio, slider, switch, textarea, navigator, audio, camera, image, video {
  box-sizing: border-box;
}

/* 全局修复：uni-input placeholder 遮挡问题 */
.uni-input-placeholder,
.input-placeholder {
  pointer-events: none !important;
  z-index: 1 !important;
}

.uni-input-input {
  pointer-events: auto !important;
  -webkit-user-select: text !important;
  user-select: text !important;
  position: relative !important;
  z-index: 100 !important;
  background: transparent !important;
}

uni-input,
.uni-input-wrapper {
  pointer-events: auto !important;
  cursor: text !important;
}

/* ============================================
   uni-input 组件 H5 端输入问题修复
   问题: placeholder 层遮挡输入层导致无法输入
   ============================================ */

/* 确保 activation-panel 下的输入框可点击 */
.activation-panel uni-input,
.activation-panel .uni-input-wrapper,
.activation-panel .uni-input-input {
  pointer-events: auto !important;
  z-index: 100 !important;
}

.activation-panel .uni-input-placeholder {
  pointer-events: none !important;
  z-index: 1 !important;
}

/* 全局修复: 所有 uni-input 的 placeholder 不应阻挡输入 */
uni-input .uni-input-placeholder,
.uni-input-wrapper .uni-input-placeholder,
.main-input .uni-input-placeholder {
  pointer-events: none !important;
  position: absolute !important;
  z-index: 1 !important;
}

/* 确保真正的输入层可交互 */
uni-input .uni-input-input,
.uni-input-wrapper .uni-input-input,
.main-input .uni-input-input {
  pointer-events: auto !important;
  -webkit-user-select: text !important;
  user-select: text !important;
  position: relative !important;
  z-index: 10 !important;
  background: transparent !important;
  width: 100% !important;
  height: 100% !important;
}

/* 修复 input-container 内的输入框 */
.input-container uni-input,
.input-container .uni-input-wrapper {
  display: block !important;
  width: 100% !important;
  position: relative !important;
}

.input-container input {
  pointer-events: auto !important;
  -webkit-user-select: text !important;
  user-select: text !important;
}

/* CSS 变量定义 */
:root, page {
  /* 主色调 - 紫蓝渐变 (保持品牌一致性) */
  --color-primary: #6366f1;
  --color-primary-light: #818cf8;
  --color-primary-dark: #4f46e5;
  --gradient-primary: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%);
  
  /* 默认背景色 - 极淡灰白 (针对白天模式优化) */
  --bg-base: #F9FAFB;
  --bg-surface: #ffffff;
  --bg-elevated: #ffffff;
  --bg-hover: #f3f4f6;
  --bg-selected-translucent: rgba(99, 102, 241, 0.08);
  --glass-blur: blur(8px);
  --border-selected: 2px solid #6366f1;
  
  /* 默认文字颜色 - 深炭灰色 */
  --text-primary: #111827;
  --text-secondary: #4B5563;
  --text-muted: #9CA3AF;
  
  /* 边框和分割线 */
  --border-color: #E5E7EB;
  --border-color-light: #F3F4F6;
  
  /* 状态颜色 */
  --color-success: #10b981;
  --color-warning: #f59e0b;
  --color-error: #ef4444;
  --color-info: #3b82f6;
  
  /* 阴影 - 柔和卡片感 */
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-glow: 0 0 40rpx rgba(99, 102, 241, 0.15);
  
  /* 圆角 */
  --radius-sm: 8rpx;
  --radius-md: 16rpx;
  --radius-lg: 24rpx;
  --radius-xl: 32rpx;
  --radius-full: 9999rpx;
  
  /* 间距 */
  --spacing-xs: 8rpx;
  --spacing-sm: 16rpx;
  --spacing-md: 24rpx;
  --spacing-lg: 32rpx;
  --spacing-xl: 48rpx;
  
  /* 字体 - 优化为更具高级感的字体栈 */
  --font-sans: 'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  --font-mono: 'SF Mono', Monaco, Consolas, monospace;
  
  /* 算力相关 */
  --quota-gradient: linear-gradient(90deg, #10b981 0%, #059669 100%);
  
  /* 分割线 */
  --divider-color: #F3F4F6;
  
  /* 图标颜色 */
  --icon-color: #4B5563;
  
  /* 输入框背景 */
  --input-bg: #ffffff;
  
  /* 卡片背景 */
  --bg-card: rgba(0, 0, 0, 0.02);
  
  /* Chat Bubbles (Default / Light) */
  --chat-ai-bg: transparent;
  --chat-ai-text: #111827;
  --chat-ai-border: transparent;
  --chat-user-bg: #e5e7eb;
  --chat-user-text: #111827;
  
  /* Sidebar (Default / Light) */
  --sidebar-bg: #f3f4f6;
  --session-active-bg: #ffffff;
  --session-active-text: #111827;
}

/* 深色模式变量覆盖 */
.dark-mode, .theme-dark {
  --bg-base: #000000;
  --bg-surface: #0e0e11;
  --bg-elevated: #0e0e11;
  --bg-hover: #2d2d4a;
  --bg-selected-translucent: rgba(124, 58, 237, 0.15);
  --glass-blur: blur(4px);
  --border-selected: 2px solid #7c3aed;
  
  --text-primary: #f8fafc;
  --text-secondary: #94a3b8;
  --text-muted: #64748b;
  
  --border-color: rgba(255, 255, 255, 0.05);
  --border-color-light: rgba(148, 163, 184, 0.08);
  
  --shadow-sm: 0 2rpx 8rpx rgba(0, 0, 0, 0.3);
  --shadow-md: 0 8rpx 24rpx rgba(0, 0, 0, 0.4);
  --shadow-lg: 0 16rpx 48rpx rgba(0, 0, 0, 0.5);
  --shadow-glow: 0 0 40rpx rgba(99, 102, 241, 0.3);
  
  --divider-color: rgba(255, 255, 255, 0.1);
  --icon-color: #94a3b8;
  --input-bg: #252542;
  --bg-card: rgba(255, 255, 255, 0.03);
  
  /* Chat Bubbles (Dark) */
  --chat-ai-bg: #18181b;
  --chat-ai-text: #e2e8f0;
  --chat-ai-border: #27272a;
  --chat-user-bg: var(--color-primary);
  --chat-user-text: #ffffff;
  
  /* Sidebar (Dark) */
  --sidebar-bg: #000000;
  --session-active-bg: #2d2d4a;
  --session-active-text: #e0e7ff;
}

/* H5 深色模式 - TabBar 和 Navigation Bar 覆盖 */
.dark-mode uni-tabbar,
.dark-mode .uni-tabbar {
  background-color: #0d0d12 !important;
  border-top-color: rgba(255, 255, 255, 0.1) !important;
}

.dark-mode uni-tabbar .uni-tabbar__bd,
.dark-mode .uni-tabbar .uni-tabbar__bd {
  background-color: #0d0d12 !important;
}

.dark-mode uni-tabbar .uni-tabbar__item .uni-tabbar__label,
.dark-mode .uni-tabbar .uni-tabbar__item .uni-tabbar__label {
  color: #94a3b8 !important;
}

.dark-mode uni-tabbar .uni-tabbar__item--active .uni-tabbar__label,
.dark-mode .uni-tabbar .uni-tabbar__item--active .uni-tabbar__label {
  color: #ffffff !important;
}

.dark-mode uni-page-head-hd,
.dark-mode .uni-page-head-hd {
  background-color: #0d0d12 !important;
}

/* H5 浅色模式 - TabBar 和 Navigation Bar 覆盖 */
.light-mode uni-tabbar,
.light-mode .uni-tabbar {
  background-color: #ffffff !important;
  border-top-color: #E5E7EB !important;
}

.light-mode uni-tabbar .uni-tabbar__bd,
.light-mode .uni-tabbar .uni-tabbar__bd {
  background-color: #ffffff !important;
}

.light-mode uni-tabbar .uni-tabbar__item .uni-tabbar__label,
.light-mode .uni-tabbar .uni-tabbar__item .uni-tabbar__label {
  color: #64748B !important;
}

.light-mode uni-tabbar .uni-tabbar__item--active .uni-tabbar__label,
.light-mode .uni-tabbar .uni-tabbar__item--active .uni-tabbar__label {
  color: #6366f1 !important;
}


.light-mode uni-page-head,
.light-mode .uni-page-head {
  background-color: #000000 !important;
}

.light-mode uni-page-head .uni-page-head__title,
.light-mode .uni-page-head .uni-page-head__title {
  color: #ffffff !important;
}


/* 极端窄屏缩放 (< 375px) */
@media screen and (max-width: 375px) {
  :root {
    --base-font-scale: 0.8;
    --base-padding-scale: 0.6;
    --spacing-xs: 6rpx;
    --spacing-sm: 12rpx;
    --spacing-md: 18rpx;
    --spacing-lg: 24rpx;
    --spacing-xl: 36rpx;
  }
  
  page {
    font-size: 24rpx;
  }
}

/* 页面基础样式 */
page {
  background: var(--bg-base);
  color: var(--text-primary);
  font-family: var(--font-sans);
  font-size: 28rpx;
  line-height: 1.6;
  min-height: 100vh;
}

/* 通用卡片样式 */
.card {
  background: var(--bg-surface);
  border-radius: var(--radius-lg);
  border: 1rpx solid var(--border-color);
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-md);
}

.card-elevated {
  background: var(--bg-elevated);
  border-radius: var(--radius-lg);
  border: 1rpx solid var(--border-color-light);
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-lg);
}

/* 按钮系统 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--radius-md);
  font-size: 28rpx;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: var(--gradient-primary);
  color: #fff;
  box-shadow: var(--shadow-glow);
}

.btn-primary:active {
  transform: scale(0.98);
  opacity: 0.9;
}

.btn-secondary {
  background: var(--bg-elevated);
  color: var(--text-primary);
  border: 1rpx solid var(--border-color);
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary);
}

.btn-ghost:active {
  background: var(--bg-hover);
}

/* 输入框样式 */
.input {
  background: var(--bg-elevated);
  border: 1rpx solid var(--border-color);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  color: var(--text-primary);
  font-size: 28rpx;
  transition: border-color 0.2s ease;
}

.input:focus {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 4rpx rgba(99, 102, 241, 0.2);
}

/* Global Input Safeguard */
input, textarea {
  pointer-events: auto !important;
  z-index: 10; /* Ensure base level visibility */
}

/* 标签系统 */
.tag {
  display: inline-flex;
  align-items: center;
  padding: 8rpx 16rpx;
  border-radius: var(--radius-full);
  font-size: 24rpx;
  background: var(--bg-elevated);
  color: var(--text-secondary);
  border: 1rpx solid var(--border-color);
}

.tag-primary {
  background: rgba(99, 102, 241, 0.15);
  color: var(--color-primary-light);
  border-color: rgba(99, 102, 241, 0.3);
}

/* 算力显示 */
.quota-badge {
  display: inline-flex;
  align-items: center;
  gap: 8rpx;
  padding: 8rpx 20rpx;
  background: rgba(34, 197, 94, 0.15);
  border: 1rpx solid rgba(34, 197, 94, 0.3);
  border-radius: var(--radius-full);
  color: var(--color-success);
  font-size: 26rpx;
  font-weight: 500;
}

/* 过渡动画 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 滚动条美化 (H5) */
::-webkit-scrollbar {
  width: 8rpx;
  height: 8rpx;
}

::-webkit-scrollbar-track {
  background: var(--bg-surface);
}

::-webkit-scrollbar-thumb {
  background: var(--bg-hover);
  border-radius: 4rpx;
}


::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

/* ============================================
   通用 hover 反馈样式 - 小程序点击交互优化
   ============================================ */

/* 透明度变暗反馈 - 适用于大多数按钮和卡片 */
.hover-fade {
  opacity: 0.7 !important;
  transition: opacity 0.08s ease-out;
}

/* 缩放反馈 - 适用于小图标按钮 */
.hover-scale {
  transform: scale(0.96) !important;
  transition: transform 0.1s ease-out;
}

/* 背景变暗反馈 - 适用于列表项 */
.hover-bg-dim {
  background-color: rgba(255, 255, 255, 0.08) !important;
  transition: background-color 0.1s ease-out;
}

.dark-mode .hover-bg-dim,
.dark-mode .session-item-hover-dark,
html.dark-mode .hover-bg-dim,
html.dark-mode .session-item-hover-dark {
  background-color: #333333 !important;
}

/* 历史记录三点菜单（Teleport 到 body，需 html 级选择器） */
html.dark-mode .session-menu-portal .session-menu-panel,
html.dark-mode .session-menu-portal--dark .session-menu-panel {
  background: #333333 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.32) !important;
}

html.dark-mode .session-menu-portal .session-menu-label,
html.dark-mode .session-menu-portal--dark .session-menu-label {
  color: #ffffff !important;
}

html.dark-mode .session-menu-portal .session-menu-divider,
html.dark-mode .session-menu-portal--dark .session-menu-divider {
  background: rgba(255, 255, 255, 0.1) !important;
}

html.dark-mode .session-menu-portal .session-menu-item-hover,
html.dark-mode .session-menu-portal .session-menu-item:hover,
html.dark-mode .session-menu-portal--dark .session-menu-item-hover,
html.dark-mode .session-menu-portal--dark .session-menu-item:hover {
  background: #4a4a4a !important;
}

html.dark-mode .session-menu-portal .session-menu-label-danger,
html.dark-mode .session-menu-portal--dark .session-menu-label-danger {
  color: #ef4444 !important;
}

/* 组合反馈 - 透明度 + 缩放 */
.hover-active {
  opacity: 0.8 !important;
  transform: scale(0.98) !important;
  transition: all 0.1s ease-out;
}

/* ============================================
   列表刷新动画
   ============================================ */

/* 列表项淡入动画 */
.list-fade-enter-active {
  animation: listFadeIn 0.3s ease-out;
}

@keyframes listFadeIn {
  from { 
    opacity: 0; 
    transform: translateY(10rpx); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
}

/* 刷新时的脉冲动画 */
.refresh-pulse {
  animation: refreshPulse 0.6s ease-in-out;
}

@keyframes refreshPulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* ============================================
   底部安全区域适配 (iPhone X+ 刘海屏)
   ============================================ */

/* 全局底部安全区域类 - 应用于所有底部固定容器 */
.safe-area-bottom {
  padding-bottom: constant(safe-area-inset-bottom) !important;
  padding-bottom: env(safe-area-inset-bottom) !important;
}

/* 底部固定容器自动适配 */
.fixed-bottom,
.batch-footer,
.bottom-action-bar {
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

/* ============================================
   图片加载防抖 (CLS Optimization)
   ============================================ */

/* 图片容器占位 - 防止布局偏移 */
.img-placeholder {
  background-color: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* 图片加载中的骨架动画 */
.img-skeleton {
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.03) 25%, 
    rgba(255, 255, 255, 0.08) 50%, 
    rgba(255, 255, 255, 0.03) 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* 常用图片尺寸占位 - 保持宽高比 */
.img-ratio-1-1 {
  aspect-ratio: 1 / 1;
}

.img-ratio-16-9 {
  aspect-ratio: 16 / 9;
}

.img-ratio-4-3 {
  aspect-ratio: 4 / 3;
}

/* 列表图片通用样式 */
.list-image {
  width: 100%;
  height: auto;
  background-color: rgba(255, 255, 255, 0.05);
  transition: opacity 0.3s ease;
}

.list-image.loading {
  opacity: 0;
}

.list-image.loaded {
  opacity: 1;
}

/* 超大屏适配：不再缩放 html/body，改缩放页面内容包装器，防止 fixed 定位失效 */
@media (min-width: 1440px) {
  .global-page-wrapper {
    zoom: 1.2;
  }
}
</style>

<style>
/* 写在 App.vue 里，任何页面都能生效 */
.force-desktop-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  background: rgba(0, 0, 0, 0.7) !important;
  z-index: 2000 !important; /* 降低层级，确保 toast (9999+) 能显示 */
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* 确保uni-app的toast始终显示在最前面 */
.uni-toast,
.uni-loading {
  z-index: 99999 !important;
}

/* 确保所有弹窗类组件的层级正确 */
.uni-modal,
.uni-actionsheet {
  z-index: 10000 !important;
}

/* 强制Toast显示在所有元素之上 */
uni-toast,
.uni-toast-wrapper {
  z-index: 999999 !important;
  position: fixed !important;
}

.force-desktop-panel {
  position: relative !important;
  width: 550px !important;
  max-width: 90vw !important;
  max-height: 85vh !important;
  transform: none !important;
  margin: 0 !important;
  left: auto !important;
  top: auto !important;
  display: flex !important;
  flex-direction: column !important;
  background: var(--bg-elevated) !important;
  border-radius: 16px !important;
  box-shadow: var(--shadow-lg) !important;
  border: 1px solid var(--border-color) !important;
}

.force-desktop-panel .topic-panel-header {
  padding: 18px 24px;
  flex-shrink: 0 !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--border-color-light);
}

.force-desktop-panel .topic-panel-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.force-desktop-panel .topic-panel-close {
  padding: 8px;
  font-size: 24px;
  line-height: 1;
  color: var(--text-muted);
  cursor: pointer;
}

/* 关键修复：针对超大屏 zoom: 1.2 的数学修正 (仅针对 >= 1440px) */
/* 1440px media query removed - handled by 1024px block with zoom:1 */

/* 
  桌面端适配通用规则 (>=1024px)：
  配合 Split Layout，给予足够的宽度和固定高度
  独立于 1440px 的 zoom 修正，确保所有宽屏都能正确显示布局
*/
@media (min-width: 1024px) {
  .force-desktop-overlay {
    /* 
       面板已移出 zoom 容器，定位回归 1:1 坐标。
    */
    width: 100vw !important; 
    height: 100vh !important;
    left: 0 !important;
    top: 0 !important;
    zoom: 1 !important; 
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .force-desktop-panel {
    height: 85vh !important; 
    width: 85vw !important; 
    max-width: 1400px !important; 
    overflow: hidden !important; 
    display: flex !important;
    flex-direction: column !important;
    z-index: 2100 !important;
  }

  
  /* 移除底部留白 */
  .force-desktop-panel .topic-panel-body {
    flex: 1 !important;
    height: 0; /* Topic Panel 依然保持固定高度 */
    min-height: 0 !important;
    overflow: hidden !important; 
    padding: 24px 24px 0 24px !important; 
  }

  /* ==== 字体适配 ==== */
  .force-desktop-panel .topic-panel-title { font-size: 16px !important; }
  .force-desktop-panel .form-label { font-size: 13px !important; }
  .force-desktop-panel .cognitive-textarea { font-size: 14px !important; line-height: 1.6 !important; }
  .force-desktop-panel .result-title { font-size: 15px !important; }
  .force-desktop-panel .viewpoint-text { font-size: 14px !important; margin-bottom: 8px !important; }
  .force-desktop-panel .section-content { font-size: 13px !important; }
}


/* 移除冲突的低优先级样式，统一在 media query 中管理 */
</style>

